import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

export type FiltersType = {
  selectedPanelsForComparison?: string[] | null;
  sourceFilterState?: string[] | null;
  selectedDates: {
    from: Date;
    to: Date;
  };
  selectedInterval: 'weekly' | 'daily' | 'monthly';
  selectedCompany: string | null;
  selectedPanel: string | null;
};

interface ReportContextType {
  selectedCompany: string | null;
  setSelectedCompany: (company: string | null) => void;
  selectedPanel: string | null;
  setSelectedPanel: (panel: string | null) => void;
  filters: FiltersType;
  setFilters: (updatedFilters: Partial<FiltersType>) => void;
  lastUpdatedAt: string;
  setLastUpdatedAt: React.Dispatch<React.SetStateAction<string>>;
}

const ReportContext = createContext<ReportContextType | undefined>(undefined);

export const useReportContext = (): ReportContextType => {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error('useReportContext must be used within a ReportProvider');
  }
  return context;
};

export const getDefaultFilters = (): FiltersType => {
  const now = new Date();
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const from = new Date(to.getFullYear(), to.getMonth(), to.getDate() - 30);

  return {
    selectedPanelsForComparison: null,
    sourceFilterState: null,
    selectedDates: { from, to },
    selectedInterval: 'weekly',
    selectedCompany: null,
    selectedPanel: null,
  };
};

const parseStateFromQuery = (query: URLSearchParams): FiltersType => {
  const defaultFilters = getDefaultFilters();

  let selectedPanelsForComparison = defaultFilters.selectedPanelsForComparison;
  try {
    const rawValue = query.get('selectedPanelsForComparison');
    if (rawValue) {
      selectedPanelsForComparison = JSON.parse(rawValue);
    }
  } catch (error) {
    console.error('Failed to parse selectedPanelsForComparison:', error);
  }

  let sourceFilterState = defaultFilters.sourceFilterState;
  try {
    const rawValue = query.get('sourceFilterState');
    if (rawValue) {
      sourceFilterState = JSON.parse(rawValue);
    }
  } catch (error) {
    console.error('Failed to parse sourceFilterState:', error);
  }

  const from = query.get('from');
  const parsedFrom = from ? new Date(from) : defaultFilters.selectedDates.from;
  const validFrom = isNaN(parsedFrom.getTime())
    ? defaultFilters.selectedDates.from
    : parsedFrom;

  const to = query.get('to');
  const parsedTo = to ? new Date(to) : defaultFilters.selectedDates.to;
  const validTo = isNaN(parsedTo.getTime())
    ? defaultFilters.selectedDates.to
    : parsedTo;

  const selectedInterval =
    (query.get('selectedInterval') as 'weekly' | 'daily' | 'monthly') ||
    defaultFilters.selectedInterval;

  const selectedCompany =
    query.get('selectedCompany') || defaultFilters.selectedCompany;
  const selectedPanel =
    query.get('selectedPanel') || defaultFilters.selectedPanel;

  return {
    selectedPanelsForComparison,
    sourceFilterState,
    selectedDates: { from: validFrom, to: validTo },
    selectedInterval,
    selectedCompany,
    selectedPanel,
  };
};

const stringifyStateToQuery = (filters: FiltersType): string => {
  const params = new URLSearchParams();
  if (filters.selectedPanelsForComparison) {
    params.set(
      'selectedPanelsForComparison',
      JSON.stringify(filters.selectedPanelsForComparison)
    );
  }
  if (filters.sourceFilterState) {
    params.set('sourceFilterState', JSON.stringify(filters.sourceFilterState));
  }
  params.set('from', filters.selectedDates?.from.toISOString());
  params.set('to', filters.selectedDates?.to.toISOString());
  params.set('selectedInterval', filters.selectedInterval);

  if (filters.selectedCompany) {
    params.set('selectedCompany', filters.selectedCompany);
  }
  if (filters.selectedPanel) {
    params.set('selectedPanel', filters.selectedPanel);
  }

  return params.toString();
};

export const ReportProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(location.search);

  const initialFilters = parseStateFromQuery(query);

  const [filters, setFilters] = useState<FiltersType>(initialFilters);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string>(null);

  // Derived values for selectedCompany and selectedPanel
  const selectedCompany = filters.selectedCompany;
  const selectedPanel = filters.selectedPanel;

  // Handlers to update selectedCompany and selectedPanel within filters
  const setSelectedCompany = (company: string | null) => {
    setFilters((prevFilters) => ({ ...prevFilters, selectedCompany: company }));
  };

  const setSelectedPanel = (panel: string | null) => {
    setFilters((prevFilters) => ({ ...prevFilters, selectedPanel: panel }));
  };

  const [debouncedUpdateQuery] = useDebouncedCallback(
    (filters: FiltersType) => {
      const queryString = stringifyStateToQuery(filters);
      navigate({ search: queryString }, { replace: true });
    },
    300
  );

  useEffect(() => {
    debouncedUpdateQuery(filters);
  }, [filters, debouncedUpdateQuery]);

  const handleFiltersChange = (updatedFilters: Partial<FiltersType>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...updatedFilters,
    }));
  };

  return (
    <ReportContext.Provider
      value={{
        selectedCompany,
        setSelectedCompany,
        selectedPanel,
        setSelectedPanel,
        filters,
        setFilters: handleFiltersChange,
        setLastUpdatedAt,
        lastUpdatedAt,
      }}
    >
      {children}
    </ReportContext.Provider>
  );
};
